import React, { useMemo } from 'react';

import { css, Stack as MuiStack, styled } from '@mui/material';

import { stackClasses } from './theme';
import { toStackDirection } from './utils';

import type { StackProps as MuiStackProps } from '@mui/material';
import type { StackOwnerState, StackProps } from './types';

const Stack = (props: StackProps) => {
  // useThemeProps 제거, 기본값을 컴포넌트에서 직접 처리
  const {
    className,
    direction = 'vertical', // theme defaultProps에서 이동 (빈 객체였으므로 기본값 설정)
    children,
    ...others
  } = props;

  const ownerState: StackOwnerState = { direction, ...props };

  const convertedDirection: MuiStackProps['direction'] = useMemo(
    () => toStackDirection(direction),
    [direction],
  );

  const validChildrenCount = useMemo(() => {
    return React.Children.toArray(children).filter(child => child != null).length;
  }, [children]);

  return (
    <StackRoot
      className={className}
      ownerState={ownerState}
      {...others}
      direction={convertedDirection}
      count={validChildrenCount}
    >
      {children}
    </StackRoot>
  );
};

export default Stack;

Stack.displayName = 'Stack';

const StackRoot = styled(MuiStack, {
  target: stackClasses.root,
  shouldForwardProp: (prop: string) =>
    prop !== 'stretch' && prop !== 'count' && prop !== 'ownerState',
})<StackProps & { count: number; ownerState: StackOwnerState }>(
  ({ direction, stretch, count }) => css`
    /* theme.ts의 styleOverrides는 비어있었으므로 기존 동적 스타일만 유지 */
    ${direction === 'column' &&
    !stretch &&
    css`
      align-items: flex-start;
    `}

    ${direction === 'row' &&
    stretch &&
    css`
      display: grid;
      grid-template-columns: repeat(${count}, 1fr);
    `}
  `,
);
